import { useState, useEffect, useRef, useCallback } from 'react';
import Editable from '../Editable';
import { resolveImg } from '../ImagePicker';
import ImageDrop from '../ImageDrop';
import { useDragReorder } from '../useDragReorder';
import { authFetch } from '../../auth';
import { ContactFormPreview } from '../FormPreview';

const SEC = '/api/cms/sections';
const SOL = '/api/cms/solutions';
const PTN = '/api/cms/partners';
const img = (k) => resolveImg(k) || null;

// 슬라이드 FLIP 애니메이션용 안정 키(렌더 전용, 저장 시 제거)
let _slideKeySeq = 0;
const nextSlideKey = () => `sk-${++_slideKeySeq}`;

let _prdKeySeq = 0;
const nextPrdKey = () => `pk-${++_prdKeySeq}`;

/**
 * 메인 — 홈 섹션(TB_PAGE_SECTION)의 텍스트를 편집·저장.
 * 카드(응용분야·파트너)는 각 관리 화면에서 편집하므로 여기선 읽기 전용 표시.
 * 제품 소개 카드는 이 화면에서 직접 추가/삭제/수정.
 */
export default function Home({ onRegisterSave, onRegisterDirty, refreshKey = 0 }) {
  const [secs, setSecs] = useState(null);
  const [sol, setSol] = useState([]);
  const [ptn, setPtn] = useState([]);
  const ref = useRef([]);
  const dirtyRef = useRef(new Set());
  // 적용분야 이미지는 언어 무관 → 다른 언어(en/ja/zhTw) applications 데이터도 보관해 함께 저장
  const appsLangRef = useRef({ en: {}, ja: {}, zhTw: {} });
  const plansLangRef = useRef({ en: {}, ja: {}, zhTw: {} });
  const heroLangRef = useRef({ en: {}, ja: {}, zhTw: {} });
  const productsLangRef = useRef({ en: {}, ja: {}, zhTw: {} });

  useEffect(() => {
    let alive = true;
    Promise.all([
      authFetch(`${SEC}?page=home&lang=ko`).then((r) => r.json()),
      authFetch(`${SOL}?lang=ko`).then((r) => r.json()),
      authFetch(`${PTN}?lang=ko`).then((r) => r.json()),
      authFetch(`${SEC}?page=home&lang=en`).then((r) => r.json()),
      authFetch(`${SEC}?page=home&lang=ja`).then((r) => r.json()),
      authFetch(`${SEC}?page=home&lang=zhTw`).then((r) => r.json()),
    ]).then(([s, so, pt, en, ja, zhTw]) => {
      if (!alive) return;
      // 히어로 슬라이드에 렌더용 안정 키 부여(순서 변경 FLIP)
      const heroSec = (s || []).find((x) => x.key === 'hero');
      if (heroSec && heroSec.data && Array.isArray(heroSec.data.slides)) {
        heroSec.data.slides = heroSec.data.slides.map((sl) => ({ ...sl, _k: nextSlideKey() }));
      }
      // 제품 소개 카드에 렌더용 안정 키 부여
      const productsSec = (s || []).find((x) => x.key === 'products');
      if (productsSec && productsSec.data && Array.isArray(productsSec.data.cards)) {
        productsSec.data.cards = productsSec.data.cards.map((c) => ({ ...c, _k: nextPrdKey() }));
      }
      ref.current = s; setSecs(s); setSol(so); setPtn(pt);
      const pick = (rows, key) => ((rows || []).find((x) => x.key === key) || {}).data || {};
      appsLangRef.current = { en: pick(en, 'applications'), ja: pick(ja, 'applications'), zhTw: pick(zhTw, 'applications') };
      plansLangRef.current = { en: pick(en, 'plans'), ja: pick(ja, 'plans'), zhTw: pick(zhTw, 'plans') };
      heroLangRef.current = { en: pick(en, 'hero'), ja: pick(ja, 'hero'), zhTw: pick(zhTw, 'hero') };
      productsLangRef.current = { en: pick(en, 'products'), ja: pick(ja, 'products'), zhTw: pick(zhTw, 'products') };
    }).catch(() => { if (alive) setSecs([]); });
    return () => { alive = false; };
  }, [refreshKey]);

  const get = (key) => (ref.current.find((s) => s.key === key) || {}).data || {};
  const updSec = (key, mut) => { dirtyRef.current.add(key); ref.current = ref.current.map((s) => (s.key === key ? { ...s, data: mut(s.data || {}) } : s)); setSecs(ref.current); };
  const f = (key, field, v) => updSec(key, (d) => ({ ...d, [field]: v }));
  const slide = (i, field, v) => updSec('hero', (d) => ({ ...d, slides: d.slides.map((s, idx) => (idx === i ? { ...s, [field]: v } : s)) }));
  const plan = (i, field, v) => updSec('plans', (d) => ({ ...d, items: d.items.map((s, idx) => (idx === i ? { ...s, [field]: v } : s)) }));
  // 메인 슬라이드: 이미지/링크(언어 무관)는 모든 언어에 동기화, 추가/삭제도 모든 언어에 적용
  const heroSyncLang = (mut) => {
    const H = heroLangRef.current;
    for (const lang of ['en', 'ja', 'zhTw']) {
      const data = H[lang] || {};
      H[lang] = { ...data, slides: mut(data.slides || []) };
    }
  };
  const setSlideImage = (i, key) => {
    updSec('hero', (d) => ({ ...d, slides: (d.slides || []).map((s, idx) => (idx === i ? { ...s, imageKey: key || undefined } : s)) }));
    heroSyncLang((arr) => arr.map((s, idx) => (idx === i ? { ...s, imageKey: key || undefined } : s)));
  };
  const slideAdd = () => {
    const base = { eyebrow: '', title: '새 슬라이드', desc: '', imageKey: '', link: '/' };
    updSec('hero', (d) => ({ ...d, slides: [...(d.slides || []), { ...base, _k: nextSlideKey() }] }));
    heroSyncLang((arr) => [...arr, { ...base }]);
  };
  const slideDel = (i) => {
    updSec('hero', (d) => ({ ...d, slides: (d.slides || []).filter((_, idx) => idx !== i) }));
    heroSyncLang((arr) => arr.filter((_, idx) => idx !== i));
  };
  // 슬라이드 드래그앤드롭 순서 변경(모든 언어 배열 동일 이동) → 저장 시 DB 반영 → 홈페이지 순서
  const reorderSlide = (from, to) => {
    const move = (arr) => { const a = [...(arr || [])]; if (from < a.length) { const [m] = a.splice(from, 1); a.splice(to, 0, m); } return a; };
    updSec('hero', (d) => ({ ...d, slides: move(d.slides) }));
    heroSyncLang(move);
  };
  const { containerRef: slideContainerRef, dragProps: slideDragProps, dropProps: slideDropProps, cardClass: slideCardClass } = useDragReorder(reorderSlide);
  // 적용 분야 아코디언 목록(별도 콘텐츠) — 문구 수정만(항목 추가/삭제 없음)
  const appItem = (i, field, v) => updSec('applications', (d) => ({ ...d, items: (d.items || []).map((s, idx) => (idx === i ? { ...s, [field]: v } : s)) }));
  // 적용 분야 옆 이미지 — 언어 무관 키이므로 ko + en/ja/zhTw 모두에 동기화
  const setAppImage = (key) => {
    updSec('applications', (d) => ({ ...d, imageKey: key || undefined }));
    const L = appsLangRef.current;
    L.en = { ...L.en, imageKey: key || undefined };
    L.ja = { ...L.ja, imageKey: key || undefined };
    L.zhTw = { ...L.zhTw, imageKey: key || undefined };
  };
  // 토탈 서비스 카드 이미지 — 카드별, 언어 무관 → ko + en/ja/zhTw items[i]에 동기화
  const setPlanImage = (i, key) => {
    updSec('plans', (d) => ({ ...d, items: (d.items || []).map((s, idx) => (idx === i ? { ...s, imageKey: key || undefined } : s)) }));
    const P = plansLangRef.current;
    for (const lang of ['en', 'ja', 'zhTw']) {
      const data = P[lang] || {};
      P[lang] = { ...data, items: (data.items || []).map((s, idx) => (idx === i ? { ...s, imageKey: key || undefined } : s)) };
    }
  };
  // 제품 소개 카드 관리 — ko 편집 + imageKey만 모든 언어에 동기화
  const prdCard = (i, field, v) => updSec('products', (d) => ({ ...d, cards: (d.cards || []).map((c, idx) => (idx === i ? { ...c, [field]: v } : c)) }));
  const addFeaturedCard = () => {
    const base = { name: '새 제품', spec: '', imageKey: '' };
    updSec('products', (d) => ({ ...d, cards: [...(d.cards || []), { ...base, _k: nextPrdKey() }] }));
    const P = productsLangRef.current;
    for (const lang of ['en', 'ja', 'zhTw']) P[lang] = { ...(P[lang] || {}), cards: [...((P[lang] || {}).cards || []), { ...base }] };
  };
  const delFeaturedCard = (i) => {
    updSec('products', (d) => ({ ...d, cards: (d.cards || []).filter((_, idx) => idx !== i) }));
    const P = productsLangRef.current;
    for (const lang of ['en', 'ja', 'zhTw']) P[lang] = { ...(P[lang] || {}), cards: ((P[lang] || {}).cards || []).filter((_, idx) => idx !== i) };
  };
  const setPrdImage = (i, key) => {
    updSec('products', (d) => ({ ...d, cards: (d.cards || []).map((c, idx) => (idx === i ? { ...c, imageKey: key || undefined } : c)) }));
    const P = productsLangRef.current;
    for (const lang of ['en', 'ja', 'zhTw']) P[lang] = { ...(P[lang] || {}), cards: ((P[lang] || {}).cards || []).map((c, idx) => (idx === i ? { ...c, imageKey: key || undefined } : c)) };
  };

  const save = useCallback(async () => {
    for (const s of ref.current) {
      if (!dirtyRef.current.has(s.key)) continue;
      // 렌더용 _k는 저장에서 제거
      let dataKo = s.data;
      if (s.key === 'hero' && dataKo && Array.isArray(dataKo.slides)) {
        dataKo = { ...dataKo, slides: dataKo.slides.map(({ _k, ...rest }) => rest) };
      }
      if (s.key === 'products' && dataKo && Array.isArray(dataKo.cards)) {
        dataKo = { ...dataKo, cards: dataKo.cards.map(({ _k, ...rest }) => rest) };
      }
      const body = { page: 'home', key: s.key, type: s.type, collection: s.collection, sort: s.sort, dataKo };
      // 이미지(언어 무관)를 다루는 섹션은 모든 언어 컬럼에 함께 저장 (텍스트 번역은 보존)
      if (s.key === 'applications') {
        const L = appsLangRef.current;
        body.dataEn = L.en; body.dataJa = L.ja; body.dataZhTw = L.zhTw;
      } else if (s.key === 'plans') {
        const L = plansLangRef.current;
        body.dataEn = L.en; body.dataJa = L.ja; body.dataZhTw = L.zhTw;
      } else if (s.key === 'hero') {
        const L = heroLangRef.current;
        body.dataEn = L.en; body.dataJa = L.ja; body.dataZhTw = L.zhTw;
      } else if (s.key === 'products') {
        const P = productsLangRef.current;
        body.dataEn = P.en; body.dataJa = P.ja; body.dataZhTw = P.zhTw;
      }
      const res = await authFetch(SEC, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`메인 저장 실패 (${res.status})`);
    }
    dirtyRef.current.clear();
  }, []);

  useEffect(() => { if (onRegisterSave) onRegisterSave(save); return () => { if (onRegisterSave) onRegisterSave(null); }; }, [save, onRegisterSave]);

  const isDirty = useCallback(() => dirtyRef.current.size > 0, []);
  useEffect(() => { if (onRegisterDirty) onRegisterDirty(isDirty); return () => { if (onRegisterDirty) onRegisterDirty(null); }; }, [isDirty, onRegisterDirty]);

  if (secs === null) {
    return <section className="section pt-page"><div className="container"><p className="heading-sub color-muted">불러오는 중…</p></div></section>;
  }

  const hero = get('hero'), apps = get('apps'), products = get('products'), applications = get('applications');
  const consult = get('consult'), plans = get('plans'), aboutco = get('aboutco'), partners = get('partners'), contact = get('contact');

  return (
    <>
      {/* 히어로 슬라이드 (카드형 편집) */}
      <section className="section hero-edit-section">
        <div className="container">
          <div className="featured-top">
            <h2>메인 슬라이드</h2>
            <span className="hero-edit-note">홈페이지에서는 슬라이드로 표시됩니다</span>
          </div>
          <div className="hero-edit-list" ref={slideContainerRef}>
            {(hero.slides || []).map((s, i) => (
              <div className={`hero-edit-card ${slideCardClass(i)}`} key={s._k || i} data-flip={s._k || `i${i}`} {...slideDragProps(i)} {...slideDropProps(i)}>
                <span className="drag-handle" title="카드를 드래그하여 순서 변경">⠿</span>
                <button type="button" className="slide-del-btn" title="슬라이드 삭제" onClick={() => slideDel(i)}>×</button>
                <ImageDrop value={s.imageKey} onChange={(k) => setSlideImage(i, k)} />
                <div className="hero-edit-fields">
                  <span className="hero-edit-no">슬라이드 {i + 1}</span>
                  <Editable as="p" className="hero-edit-eyebrow" value={s.eyebrow} onChange={(t) => slide(i, 'eyebrow', t)} />
                  <Editable as="h3" className="hero-edit-title" value={s.title} onChange={(t) => slide(i, 'title', t)} />
                  <Editable as="p" className="hero-edit-desc" value={s.desc} onChange={(t) => slide(i, 'desc', t)} />
                </div>
              </div>
            ))}
          </div>
          <button type="button" className="btn-pill sm" style={{ marginTop: 14 }} onClick={slideAdd}>+ 슬라이드 추가</button>
        </div>
      </section>

      {/* 응용 분야 (제목/버튼 편집 · 카드는 응용분야 관리에서) */}
      <section className="section">
        <div className="container">
          <div className="featured-top">
            <Editable as="h2" value={apps.title || ''} onChange={(t) => f('apps', 'title', t)} />
          </div>
          <div className="svc-cards">
            {sol.map((s) => (
              <div className="s-card" key={s.slug}>
                {s.imageKey && <img src={img(s.imageKey)} alt={s.title} loading="lazy" />}
                <h3>{s.title}</h3>
                <div className="s-price">{s.subtitle}</div>
                {s.desc && <p>{s.desc}</p>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 제품 소개 */}
      <section className="featured section">
        <div className="container">
          <div className="featured-top"><Editable as="h2" value={products.title || ''} onChange={(t) => f('products', 'title', t)} /></div>
          <div className="products-grid">
            {(products.cards || []).map((p, i) => (
              <div className="p-card" key={p._k || i}>
                <button type="button" className="slide-del-btn" title="카드 삭제" onClick={() => delFeaturedCard(i)}>×</button>
                <ImageDrop value={p.imageKey} onChange={(k) => setPrdImage(i, k)} />
                <Editable as="div" className="p-name" value={p.name || ''} onChange={(t) => prdCard(i, 'name', t)} />
                <Editable as="div" className="p-price" value={p.spec || ''} onChange={(t) => prdCard(i, 'spec', t)} />
              </div>
            ))}
            <button type="button" className="card-add-tile" onClick={addFeaturedCard}>+ 카드 추가</button>
          </div>
        </div>
      </section>

      {/* 적용 분야 (제목/설명/버튼) */}
      <section className="our-services section">
        <div className="container">
          <div className="svc-layout">
            <div className="svc-text">
              <Editable as="h2" value={applications.title || ''} onChange={(t) => f('applications', 'title', t)} />
              <Editable as="p" className="desc" value={applications.desc || ''} onChange={(t) => f('applications', 'desc', t)} />
              <div className="home-apps-edit">
                {(applications.items || []).map((it, i) => (
                  <div className="home-apps-item" key={i}>
                    <div className="home-apps-item-body">
                      <Editable as="h4" value={it.title || ''} onChange={(t) => appItem(i, 'title', t)} />
                      <Editable as="p" value={it.body || ''} onChange={(t) => appItem(i, 'body', t)} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="svc-img">
              <ImageDrop value={applications.imageKey} onChange={setAppImage} />
            </div>
          </div>
        </div>
      </section>

      {/* 상담 CTA */}
      <section className="consult">
        <div className="consult-bg"><img src="/images/misc/consult-bg.png" alt="" loading="lazy" /></div>
        <div className="consult-inner">
          <Editable as="h2" value={consult.title || ''} onChange={(t) => f('consult', 'title', t)} />
          <Editable as="p" value={consult.desc || ''} onChange={(t) => f('consult', 'desc', t)} />
        </div>
      </section>

      {/* 토탈 서비스 플랜 */}
      <section className="choose-plan section">
        <div className="container">
          <Editable as="h2" value={plans.title || ''} onChange={(t) => f('plans', 'title', t)} />
          <Editable as="p" className="heading-sub color-muted" value={plans.subtitle || ''} onChange={(t) => f('plans', 'subtitle', t)} />
          <div className="plans-grid">
            {(plans.items || []).map((p, i) => (
              <div className="plan-card" key={i}>
                <ImageDrop value={p.imageKey} onChange={(k) => setPlanImage(i, k)} />
                <div className="plan-body">
                  <Editable as="h3" value={p.title} onChange={(t) => plan(i, 'title', t)} />
                  <Editable as="div" className="price" value={p.step} onChange={(t) => plan(i, 'step', t)} />
                  <Editable as="div" className="period" value={p.period} onChange={(t) => plan(i, 'period', t)} />
                  <Editable as="p" className="plan-desc" value={p.desc} onChange={(t) => plan(i, 'desc', t)} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AceTec 소개 */}
      <section className="about-co section">
        <div className="container">
          <div className="about-grid">
            <div className="about-img"><img src="/images/about/about-company.png" alt="회사 소개" loading="lazy" /></div>
            <div className="about-txt">
              <Editable as="h2" value={aboutco.title || ''} onChange={(t) => f('aboutco', 'title', t)} />
              <Editable as="p" value={aboutco.desc || ''} onChange={(t) => f('aboutco', 'desc', t)} />
            </div>
          </div>
        </div>
      </section>

      {/* 기술 파트너사 */}
      <section className="social section">
        <div className="container">
          <Editable as="h2" value={partners.title || ''} onChange={(t) => f('partners', 'title', t)} />
          <div className="social-grid">
            {ptn.filter((p) => !p.type || p.type === 0).map((p) => (p.logoKey ? <img key={p.name} src={img(p.logoKey)} alt={p.name} loading="lazy" /> : null))}
          </div>
        </div>
      </section>

      {/* 문의하기 */}
      <section className="contact-sec section">
        <div className="container">
          <div className="contact-grid">
            <div className="contact-txt">
              <Editable as="h2" value={contact.title || ''} onChange={(t) => f('contact', 'title', t)} />
              <Editable as="p" value={contact.desc || ''} onChange={(t) => f('contact', 'desc', t)} />
            </div>
            <ContactFormPreview />
          </div>
        </div>
      </section>
    </>
  );
}
